<template>
  <div class="exam">
    <template v-if="isArticleExam">
      <div class="describe-wrapper" v-if="isComparison">
        <el-tag class="describe">
          <slot name="describe"></slot>
        </el-tag>
      </div>
      <van-article-exam
        :time="secondsToTime"
        :learn-mode="quizType"
        :quizzes="exercises"
        :pre-scene="scene"
        @replay="handleArticleReplay"
        @submit="handleArticleExam"
        @finished="handleFinished"
        @learnNext="learnNext"
        @openCertificate="openCertificate"
        @continue="handleContinue" />
    </template>
    <template v-else>
      <van-exam-result
        v-if="isShowResult && !isPreview"
        :results="results"
        :title="name"
        :score="score"
      />
      <template v-else>
        <div class="exam-wrap" :class="{full : !control}" ref="content">
          <div class="timer" v-if="!isPreview">{{secondsToTime}}</div>
          <div class="quiz-group" v-for="(group, groupIndex) in groupExercises" :key="groupIndex">
            <p class="quiz-name">
              {{getQuizName(group.code)}}
              <slot name="describe"></slot>
              <span
                class="quiz-tips"
                v-if="group.code === 'DCPX' && (isLearnBeforeTest || isLearnAfterTest)">
                （写出你已会的单词，输入回车可以进入下一题）
              </span>
            </p>
            <!-- 学前测试提示语 -->
            <p class="before-exam-tips" v-if="!groupIndex && isLearnBeforeTest">
              <slot name="before-exam-tips"></slot>
            </p>
            <div
              class="quiz-item"
              v-for="(exercise, index) in group.exercises"
              :key="index"
              ref="quizItem"
            >
              <span class="index">{{index + 1}}.</span>
              <component
                class="component"
                :class="{preview: isPreview, 'no-answer': isPreview && !exercise.userAnswer}"
                :key="index"
                :id="exercise.id"
                :is="getComponent(exercise.code)"
                :exercise="exercise"
                :is-done="isPreview"
                :is-show-hint="false"
                :mode="exercise.mode"
                :user-answer="exercise.userAnswer"
                is-exam
                @change="handleChange($event, exercise, exercise.index)"
                :autoFocus="false"
                :autoPlay="false"
                @complete="handleComplete(exercise, exercise.index)"
                ref="quizzes"
              />
            </div>
          </div>
        </div>
        <div class="control" v-if="control">
          <template v-if="!isPreview">
            <span class="tips">点击【交卷】可提交并计算成绩。</span>
            <el-button type="primary" @click="submit">交卷</el-button>
          </template>
          <template v-else>
            <el-button
              v-if="score >= 100 && isLearnBeforeTest"
              type="primary"
              @click="openCertificate">
              查看奖状
            </el-button>
            <el-button
              v-if="isShowNextUnitButton"
              @click="learnNext"
              type="primary">学习下一单元</el-button>
            <slot name="control"></slot>
          </template>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { LEARN_AFTER_RIGHT_RESULTS } from '@/store/quiz/types';
import utils from '@/services/utils';
import api from '@/api/graphql/englishCourse';
import CHXZ from 'vanthink-components/components/quiz/CHXZ.vue';
import DCTX from 'vanthink-components/components/quiz/DCTX.vue';
import SK from 'vanthink-components/components/quiz/SK.vue';
import HYDC from 'vanthink-components/components/quiz/HYDC.vue';
import DCPX from 'vanthink-components/components/quiz/DCPX.vue';
import VanExamResult from '@/components/quizzes/bdc/ExamResult.vue';
import VanArticleExam from '@/components/quizzes/bdc/ArticleExam.vue';
import { Tag } from 'element-ui';


const COMPONENTS = {
  CHXZ,
  DCTX,
  SK,
  HYDC,
  DCPX,
};

export default {
  props: {
    // exam name
    name: {
      type: String,
      default: '',
    },
    exercises: {
      type: Array,
      default() {
        return [];
      },
    },
    quizType: {
      type: String,
      default: '',
    },
    // Identify the apis that need to be accessed
    mode: {
      type: String,
      default: '',
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
    control: {
      type: Boolean,
      default: true,
    },
    courseId: {
      type: Number,
      default: 0,
    },
    unitId: {
      type: Number,
      default: 0,
    },
    extend: {
      type: Object,
      default() {
        return {
          results: [],
          score: 0,
          response: null,
        };
      },
    },
    scene: {
      type: Number,
      default: 2,
    },
    isComparison: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CHXZ,
    DCTX,
    SK,
    HYDC,
    DCPX,
    VanExamResult,
    VanArticleExam,
    ElTag: Tag,
  },
  inject: ['getUnitList', 'getCurrentUnitId'],
  data() {
    return {
      score: 0,
      seconds: 0,
      results: [],
      isShowResult: false,
      response: null,
    };
  },
  computed: {
    ...mapGetters({
      rightResults: LEARN_AFTER_RIGHT_RESULTS,
    }),
    secondsToTime() {
      return utils.secondsToTime(this.seconds);
    },
    isLearnBeforeTest() {
      return this.quizType === 'learn_before_test';
    },
    isLearnAfterTest() {
      return this.quizType === 'learn_after_test';
    },
    groupExercises() {
      let index = 0;

      return this.exercises.reduce((prev, cur) => {
        if (prev.length) {
          const [lastArray] = prev.slice(-1);
          const [lastItem] = lastArray.slice(-1);
          if (lastItem.code === cur.code) {
            lastArray.push(cur);
            return prev;
          }
          return [...prev, [cur]];
        }
        return [[cur]];
      }, [])
        .map((group) => {
          const [exercise] = group.map((item) => {
            index += 1;
            return {
              ...item,
              index,
            };
          });
          return {
            code: exercise.code,
            exercises: group,
          };
        });
    },
    isArticleExam() {
      return this.exercises.every(item => item.code === 'YDLJ');
    },
    isShowNextUnitButton() {
      if (this.getUnitList && this.getCurrentUnitId) {
        const unitList = this.getUnitList();
        const currentUnitId = this.getCurrentUnitId();
        const index = utils.Array.findIndex(unitList, { id: currentUnitId }, 'id');
        const isLast = index >= unitList.length - 1;
        return !isLast && this.isLearnBeforeTest && this.score >= 100;
      }
      return false;
    },
  },
  methods: {
    ...mapMutations({
      setRightResults: LEARN_AFTER_RIGHT_RESULTS,
    }),
    submit() {
      const handler = () => {
        /* eslint-disable prefer-destructuring */
        const group = utils.Array.groupBy(this.results, 'id');
        const results = group.map((itemArray) => {
          const [defaultItem] = itemArray;
          // one word, more quiz
          const isMultiple = itemArray.length > 1;

          const answer = JSON.stringify(itemArray.map((item) => {
            const basicParams = {
              code: item.ref.code,
              mode: item.ref.mode,
              answer: item.ref.answer, // 小题的正确答案
              userAnswer: item.answer, // 用户选择/输入的答案
            };
            const extend = {};
            if (item.ref.code === 'CHXZ') {
              extend.options = item.ref.options;
            }
            return {
              ...basicParams,
              ...extend,
            };
          }));

          return {
            id: defaultItem.id,
            answer,
            status: isMultiple
              ? +itemArray.every(item => item.isRight)
              : +!!defaultItem.isRight,
            errorCount: isMultiple
              ? itemArray.filter(item => !item.isRight).length
              : +!defaultItem.isRight,
          };
        });

        this.submitResult(results);
      };
      utils.confirm('确定要交卷吗，要不要再检查一下？', '提示')
        .then(() => {
          handler();
        });
    },
    handleChange({ isRight, isComplete, answer }, exercise, index) {
      utils.Array.pushReplace(this.results, {
        id: exercise.id,
        answer,
        isRight,
        isComplete,
        key: `${exercise.id}_${exercise.code}_${exercise.mode}`,
        ref: exercise,
        index,
      }, 'key');
      this.results.sort((a, b) => a.index - b.index);
    },
    handleComplete(exercise, index) {
      const component = this.$refs.quizzes[index + 1];
      if (component && component.getFocus) {
        component.getFocus();
      }
      if (component && component.playAudio && exercise.code === 'DCTX') {
        component.playAudio();
      }
    },
    handleArticleExam({ results, callback }) {
      this.submitResult(results, callback);
    },
    submitResult(results, callback) {
      if (this.quizType === 'learn_after_test' && this.rightResults.length) {
        results.push(...this.rightResults.filter(result => result.unitId === this.unitId));
      }
      api.submitCourseResult({
        courseId: this.courseId,
        unitId: this.unitId,
        type: `bdc_${this.quizType}`,
        mode: this.mode,
        seconds: this.seconds,
        spend: '',
        quizzes: results,
      }).then(({ englishCourseResult }) => {
        this.destroyTimer();
        this.score = englishCourseResult.score;
        this.response = englishCourseResult;

        if (this.isArticleExam && callback) {
          callback({
            score: englishCourseResult.score,
            isContinue: englishCourseResult.isContinue,
            beforeExamId: englishCourseResult.beforeExamId,
          });
          return;
        }

        if (this.quizType === 'learn_after_test') {
          /*
          * 在错题再练中props exercises只会传递学后测试中做错的小题
          * 保存结果时需要携带上之前作对的小题
          * 将答对的小题存入store，若多次进行错题再练则需要累加每次答对的小题id
          * 存入时携带unitId，区分是哪个单元的错题再练，若不作区分连续进行多个单元多的错题在练时，会混淆
          * */
          if (this.score >= 100) {
            this.setRightResults({
              learnAfterTestRightResults: [],
            });
          } else {
            this.setRightResults({
              learnAfterTestRightResults: [
                ...this.rightResults,
                ...results
                  .filter(result => result.status && !utils.Array.exist(this.rightResults, result, 'id'))
                  .map(result => ({
                    ...result,
                    unitId: this.unitId,
                  })),
              ],
            });
          }
        } else {
          this.isShowResult = true;
        }

        this.$emit('submitted', {
          score: this.score,
          results: this.results,
          response: englishCourseResult,
        });
      }, () => {
        this.destroyTimer();
      });
    },
    getComponent(code) {
      return COMPONENTS[code];
    },
    getQuizName(code, mode = 0) {
      return {
        CHXZ: {
          0: '词汇选择',
          1: '词汇选择（选解释）',
          2: '词汇选择（选单词）',
          3: '听音选词',
          4: '听音选义',
        },
        DX: {
          0: '单项选择',
        },
        HYDC: {
          0: '还原单词',
        },
        DCTX: {
          0: '单词听写',
        },
        SK: {
          0: '闪卡练习',
          1: '闪卡练习（学习模式）',
          2: '闪卡练习（抄写模式）',
          3: '闪卡练习（录音模式）',
        },
        HYDX: {
          0: '还原单词',
        },
        DCPX: {
          0: '单词拼写',
          1: '单词拼写（随机模式）',
          2: '单词拼写（默写模式）',
          3: '单词拼写（自定义模式）',
        },
        TYLJ: {
          0: '听音连句',
        },
        BQWZ: {
          0: '补全文章',
        },
        JXZH: {
          0: '句型转换',
        },
        QHLJ: {
          0: '强化炼句',
          1: '强化炼句（简单模式）',
          2: '强化炼句（复杂模式）',
          3: '强化炼句（默写模式）',
          4: '强化炼句（自定义模式）',
        },
        YDLJ: {
          0: '阅读理解',
        },
        XCTK: {
          0: '选词填空',
        },
        THXZ: {
          0: '听后选择',
        },
        WXTK: {
          0: '完形填空',
        },
      }[code][mode];
    },
    startTimer() {
      this.destroyTimer();
      this.timerInstance = setInterval(() => {
        this.seconds += 1;
      }, 1000);
    },
    destroyTimer() {
      if (this.timerInstance) {
        clearInterval(this.timerInstance);
        this.timerInstance = null;
      }
    },
    initResults() {
      this.results = this.exercises.map((exercise, index) => ({
        id: exercise.id,
        answer: '',
        isRight: false,
        isComplete: false,
        key: `${exercise.id}_${exercise.code}_${exercise.mode}`,
        ref: exercise,
        index,
      }));
    },
    handleArticleReplay() {
      this.seconds = 0;
      this.startTimer();
    },
    openCertificate() {
      this.$emit('openCertificate', {
        score: this.score,
        results: this.results,
        response: this.response,
        isArticleExam: this.isArticleExam,
      });
    },
    handleFinished() {
      this.$emit('submitted', {
        score: this.score,
        results: this.results,
        response: this.response,
        isArticleExam: this.isArticleExam,
      });
    },
    learnNext() {
      this.$emit('learnNext');
    },
    handleContinue() {
      this.$emit('continue');
    },
  },
  created() {
    if (this.isPreview) {
      const { results, score, response } = this.extend;
      if (results) {
        this.results = results;
      } else {
        this.initResults();
      }
      if (score) {
        this.score = score;
      }
      if (response) {
        this.response = response;
      }
    } else {
      this.initResults();
    }
  },
  mounted() {
    if (!this.isPreview) {
      this.startTimer();
    }
  },
  beforeDestroy() {
    this.destroyTimer();
  },
};
</script>

<style lang="scss" scoped>
  @import '@/styles/variables.scss';

  .exam {
    position: relative;
    height: 100%;
    overflow-y: auto;
    .timer {
      position: absolute;
      top: 10px;
      right: 0;
      transform: translateX(-50%);
      font-size: 22px;
      color: $primary;
    }
    .describe-wrapper {
      position: absolute;
      right: 10px;
      top: 10px;
      font-size: 24px;
      .describe {
        font-size: 18px;
      }
    }
    .control {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 60px;
      line-height: 60px;
      padding: 0 20px;
      text-align: right;
      .tips {
        float: left;
        /deep/ span {
          color: $primary;
          margin: 0 4px;
          font-weight: bold;
        }
        .iconfont {
          font-size: 22px;
          margin: 0 4px;
          color: grey;
        }
      }
      i {
        position: relative;
        top: -2px;
        font-size: 48px;
        color: $primary;
        vertical-align: middle;
        cursor: pointer;
        &.disabled {
          color: rgba($grey-color, .2);
          cursor: not-allowed;
          &:active {
            color: rgba($grey-color, .3);
          }
        }
        &:active {
          color: $primary-light;
        }
      }
    }
    .answer-card {
      bottom: 60px;
    }
    .exam-wrap {
      box-sizing: border-box;
      height: calc(100% - 65px);
      overflow-y: auto;
      &.full {
        height: 100%;
        padding-bottom: 30px;
      }
      .quiz-group {
        .quiz-name {
          font-size: 24px;
          padding: 10px 0 10px 20px;
          margin: 20px 0;
          border-left: 6px solid $primary;
          background-color: rgb(250, 250, 250);
          text-align: left;
          &:first-child {
            margin-top: 0;
          }
          .quiz-tips {
            font-size: 16px;
            color: grey;
          }
        }
        .before-exam-tips {
          text-align: left;
          padding-left: 20px;
          font-size: 16px;
          color: grey;
          .tips {
            /deep/ span {
              color: $primary;
            }
          }
        }
      }
      .quiz-item {
        position: relative;
        text-align: left;
        margin-bottom: 10px;
        .index {
          position: absolute;
          width: 50px;
          text-align: right;
          font-size: 22px;
          height: 30px;
          line-height: 30px;
        }
        .component {
          padding-left: 60px;
          margin: 0;
        }
        .chxz-container {
          max-width: 80%;
          height: auto;
          &.no-answer {
            /deep/ .title {
              &:after {
                display: inline-block;
                content: '(未作答)';
                color: $danger;
                margin-left: 10px;
              }
            }
          }
          /deep/ .title {
            margin: 0 0 10px 0;
            text-align: left;
            line-height: normal;
            font-size: 22px;
            .word {
              float: left;
              font-size: 22px;
              height: auto;
              text-align: left;
            }
            .speaker {
              margin-left: 5px;
            }
            &:after {
              display: block;
              content: '';
              clear: both;
            }
          }
          /deep/ .options {
            list-style: inside;
            list-style-type: upper-alpha;
            width: 100%;
            margin: 0;
            padding-bottom: 20px;
            li {
              margin: 0;
              padding: 0;
              text-align: left;
              background-color: transparent;
              box-shadow: 0 0 0;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
          }
        }
        .dctx-container {
          height: auto;
          margin: 0;
          &.preview {
            /deep/ .main {
              height: 140px;
            }
          }
          /deep/ .main {
            position: relative;
            margin-top: 0;
            display: block;
            height: 80px;
            .text-input {
              position: relative;
              top: 40px;
              margin: 0;
              padding: 0 5px 5px;
              font-size: 26px;
              width: 260px;
              min-height: 30px;
              word-break: break-word;
              text-align: left;
              border-bottom: 2px solid $primary;
              &.error {
                border-color: $danger;
              }
            }
            .text {
              position: absolute;
              top: 0;
              font-size: 22px;
              display: inline-block;
              margin: 0;
              .speaker {
                vertical-align: middle;
              }
              .word {
                margin-left: 10px;
                vertical-align: middle;
              }
            }
            .explain {
              position: relative;
              top: 60px;
              font-size: 22px;
              margin: 0;
            }
          }
        }
        .dcpx-container {
          height: auto;
          /deep/ .main {
            display: inline-block;
            margin: 0;
            padding-bottom: 10px;
            .text-input {
              width: auto;
              padding: 20px 5px 5px;
              font-size: 26px;
              text-align: left;
              background-color: transparent;
              box-shadow: 0 0 0 transparent;
              border-bottom: 2px solid $primary;
              border-radius: 0;
              color: black !important;
              &.error {
                border-width: 0 0 2px 0;
                border-style: solid;
                border-color: $danger;
              }
            }
            .user-answer {
              display: none;
            }
          }
          /deep/ .answer {
            display: inline-block;
            width: auto;
            font-size: 22px;
            margin: 0;
            color: $success;
            &:before {
              content: '(';
            }
            &:after {
              content: ')';
            }
          }
          /deep/ .explain {
            font-size: 22px;
            width: auto;
            margin: 0;
            text-align: left;
            line-height: normal;
            color: grey;
            .speaker {
              vertical-align: text-top;
            }
          }
        }
      }
    }
  }
</style>
